import React, { useState } from 'react';
import { Box, Typography, MenuItem, Select } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { STATUS_ORDER, STATUS_LABEL, activeTotal } from '../../utils/memberStatus';
import { COLORS, pct, fmt } from './signupReportUtils';

const METRICS = {
  signed: { label: 'Signed Up', color: COLORS.signed },
  active: { label: 'Active (Approved + Hidden)', color: COLORS.active },
  ...STATUS_ORDER.reduce((acc, k) => ({ ...acc, [k]: { label: STATUS_LABEL[k], color: COLORS[k] } }), {}),
};

// Active is derived (Approved + Hidden); every other metric is a stored count.
const valOf = (r, metric) => (metric === 'active' ? activeTotal(r) : r[metric] || 0);

const TOP_OPTIONS = [10, 20, 50];

const TopNToggle = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', bgcolor: '#fff', border: '1px solid #EAEAEA', borderRadius: '10px', p: '3px' }}>
    {TOP_OPTIONS.map((n) => (
      <Box
        key={n}
        onClick={() => onChange(n)}
        sx={{
          fontSize: 12, fontWeight: 600, px: 1.5, py: 0.7, borderRadius: '7px', cursor: 'pointer',
          color: value === n ? '#fff' : '#737373', bgcolor: value === n ? '#171717' : 'transparent',
        }}
      >
        Top {n}
      </Box>
    ))}
  </Box>
);

const GeoBarChart = ({ title, rows, nameKey, metric, topN }) => {
  const meta = METRICS[metric];
  // Grand total across ALL regions → the denominator for the share %, so the
  // percentages represent each region's slice of the whole (sum = 100%).
  const total = rows.reduce((s, r) => s + valOf(r, metric), 0);
  const ranked = [...rows]
    .map((r) => ({ name: r[nameKey] || 'Unknown', count: valOf(r, metric) }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count) // sort by COUNT, descending
    .slice(0, topN);
  const shown = ranked.reduce((s, r) => s + r.count, 0);

  const options = {
    chart: { type: 'bar', height: Math.max(260, ranked.length * 34 + 60), backgroundColor: 'transparent' },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: { categories: ranked.map((r) => r.name), labels: { style: { fontSize: '11px', fontWeight: '600', color: '#404040' } } },
    yAxis: {
      min: 0,
      title: { text: null },
      gridLineColor: '#F0F0F0',
      labels: { formatter() { return fmt(this.value); }, style: { fontSize: '10px', color: '#737373' } },
    },
    tooltip: {
      formatter() {
        const r = ranked[this.point.index];
        return `<b>${this.x}</b>: ${fmt(r.count)} (${pct(r.count, total)}% of ${fmt(total)} ${meta.label})`;
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 5,
        color: meta.color,
        dataLabels: {
          enabled: true,
          formatter() {
            const r = ranked[this.point.index];
            return `${fmt(r.count)}  ·  ${pct(r.count, total)}%`;
          },
          style: { fontSize: '11px', fontWeight: '700', color: '#404040', textOutline: 'none' },
        },
      },
    },
    series: [{ name: meta.label, data: ranked.map((r) => r.count) }],
  };

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #EAEAEA', borderRadius: '14px', p: 2.2 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#171717' }}>{title}</Typography>
      <Typography sx={{ fontSize: 11.5, color: '#737373', mb: 2 }}>
        {meta.label} by count · % = share of {fmt(total)} total
        {total ? ` · top ${ranked.length} = ${pct(shown, total)}%` : ''}
      </Typography>
      {ranked.length ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : (
        <Typography sx={{ textAlign: 'center', py: 4, color: '#A3A3A3' }}>No data available</Typography>
      )}
    </Box>
  );
};

const SignupGeoCharts = ({ byCity = [], byCountry = [] }) => {
  const [metric, setMetric] = useState('signed');
  const [topN, setTopN] = useState(10);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', color: '#737373' }}>
          Geographic comparison
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Select
            size="small"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            sx={{ fontSize: 12.5, fontWeight: 600, color: '#404040', bgcolor: '#fff', borderRadius: '10px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#EAEAEA' } }}
          >
            {Object.entries(METRICS).map(([k, m]) => (
              <MenuItem key={k} value={k} sx={{ fontSize: 13 }}>{m.label}</MenuItem>
            ))}
          </Select>
          <TopNToggle value={topN} onChange={setTopN} />
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <GeoBarChart title="By city" rows={byCity} nameKey="city" metric={metric} topN={topN} />
        <GeoBarChart title="By country" rows={byCountry} nameKey="country" metric={metric} topN={topN} />
      </Box>
    </Box>
  );
};

export default SignupGeoCharts;
